import { Link } from "react-router-dom";
import { useConfirm, useShowQuickTips } from "common/hooks";
import { factoryFertiliser } from "common/domain";
import * as utils from "common/utils";
import * as domain from "common/domain";
import { WriteMode } from "components/AnalysisRenderMode";
import TileSubPanel from "components/TileSubPanel";
import Alert from "components/Alert";
import ActionLink from "components/ActionLink";
import { ZendeskLink } from "components/Help";
import ScenarioToolLink from "containers/ScenarioTool/ScenarioToolLink";
import { FertilisersGreenBox } from "containers/BudgetHome/Fertiliser/FertilisersGreenBox";
import TabMessages from "containers/BudgetHome/TabMessages";
import { TAB } from "containers/BudgetHome/PageTabs";
import * as cropUtils from "containers/BudgetHome/Crops/_utils";
import { useUpdateAnalysisAsync } from "containers/hooks";
import BlockListCard from "./BlockListCard";
import BlockListGroup from "./BlockListGroup";
import { useFertiliserProductModal } from "./ProductModal";
import AdditiveList from "./AdditiveList";
import * as fertUtils from "./_utils";

export default function Fertiliser({ farm, analysis }) {
    const showQuickTips = useShowQuickTips();
    const confirm = useConfirm();
    const [fertiliserProductModal, openFertiliserProductModal] = useFertiliserProductModal();
    const updateAnalysisAsync = useUpdateAnalysisAsync(analysis);

    fertUtils.updateApplicationModel(analysis);
    const { blocks = [] } = analysis;
    const blockList = utils.getBlockList(blocks);
    const hasDCD = (analysis.nitrateInhibitors || []).length > 0;

    const groupByMonthResown = (blocks) => {
        return blocks.reduce((groups, block) => {
            let blockGroup = groups.find((g) => g.monthResown === block.monthResown);
            if (!blockGroup) {
                blockGroup = {
                    blocks: [],
                    monthResown: block.monthResown,
                    type: block.type,
                    calendar: cropUtils.rotationYear(block),
                };
                groups.push(blockGroup);
            }
            blockGroup.blocks.push(block);
            return groups;
        }, []);
    };

    const groupApplicationsConfirm = () => {
        confirm(`Merging applies identical kg/ha applications to a set of blocks. Are you sure you want to merge?`, async () => {
            analysis.fertiliser.forEach((fert) => {
                fert.applications = fertUtils.groupApplications(fert.applications);
            });
            await updateAnalysisAsync(analysis);
        });
    };

    const ungroupApplicationsConfirm = () => {
        confirm(`Splitting will create individual applications for each block based on kg/ha applications. Are you sure you want to split?`, async () => {
            analysis.fertiliser.forEach((fert) => {
                fert.applications = fertUtils.ungroupApplications(fert.applications);
            });
            await updateAnalysisAsync(analysis);
        });
    };

    const addFertilser = (material) => {
        const { fertiliser = [] } = analysis;
        const filteredData = fertiliser.filter((item) => item.material === "Product" && item.manufacturerId !== 1);

        const manufacturerCounts = filteredData.reduce((acc, item) => {
            acc[item.manufacturerId] = (acc[item.manufacturerId] || 0) + 1;
            return acc;
        }, {});

        const mostUsedManufacturerId = Object.entries(manufacturerCounts).reduce((max, [id, count]) => (count > max.count ? { id, count } : max), { id: null, count: 0 });

        const fert = factoryFertiliser();
        fert.id = "NEW";
        if (mostUsedManufacturerId.id) {
            const mostUsedManufacturer = filteredData.find((item) => String(item.manufacturerId) === String(mostUsedManufacturerId.id));
            fert.manufacturerId = mostUsedManufacturer?.manufacturerId;
            fert.manufacturer = mostUsedManufacturer?.manufacturer;
        }
        const viewModel = {
            material,
            farm,
            analysis,
            fertiliser: fert,
            isLastPreviousLime: false,
            customCompositions: [],
            isNew: true,
        };
        openFertiliserProductModal(viewModel);
    };

    const fruitBlocks = { type: "ProductiveFruit", blocks: blocks.filter((b) => b.type === "ProductiveFruit"), calendar: domain.farmYear };
    const pastureBlocks = { type: "ProductivePasture", blocks: blocks.filter((b) => b.type === "ProductivePasture"), calendar: domain.farmYear };
    const fodderCropGroups = groupByMonthResown(blocks.filter((b) => b.type === "FodderCrop"));
    const productiveCropGroups = groupByMonthResown(blocks.filter((b) => b.type === "ProductiveCrop"));
    const blockGroups = [];
    const cropBlocks = [...fodderCropGroups, ...productiveCropGroups];
    if (fruitBlocks.blocks.length > 0) blockGroups.push(fruitBlocks);
    if (pastureBlocks.blocks.length > 0) blockGroups.push(pastureBlocks);
    cropBlocks.forEach((g) => blockGroups.push(g));

    return (
        <>
            <TabMessages farm={farm} analysis={analysis} tab={TAB.FERTILISER} />
            <div className="ActionsBar ActionsBar--super u-print-none">
                <ul className="ActionsBar-links">
                    <WriteMode>
                        <li>
                            <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-product" onClick={() => addFertilser("Product")}>
                                <span>Add product</span>
                            </ActionLink>
                        </li>
                        <li>
                            <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-lime" onClick={() => addFertilser("Lime")}>
                                <span>Add lime</span>
                            </ActionLink>
                        </li>
                        <li>
                            <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-organic" onClick={() => addFertilser("Organic")}>
                                <span>Add organic</span>
                            </ActionLink>
                        </li>
                        <li>
                            <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-soluble" onClick={() => addFertilser("Soluble")}>
                                <span>Add soluble</span>
                            </ActionLink>
                        </li>
                        {!hasDCD && (
                            <li>
                                <Link to={`/app/farm/${farm.id}/analysis/${analysis.id}/fertiliser/dcd`} className="IconLink--arrow-plus" id="fertiliser-add-dcd">
                                    <span>Add DCD</span>
                                </Link>
                            </li>
                        )}
                    </WriteMode>
                </ul>

                <ul className="ActionsBar-links">
                    <WriteMode>
                        <li>
                            <Link className="IconLink--arrow-plus" id="fertiliser-add-applications" to={`/app/farm/${farm.id}/analysis/${analysis.id}/fertiliser/applications`}>
                                <span>Add fertiliser applications</span>
                            </Link>
                        </li>
                        <li>
                            <ActionLink className="IconLink--group" id="fertiliser-group" onClick={() => groupApplicationsConfirm()}>
                                <span>Merge applications</span>
                            </ActionLink>
                        </li>
                        <li>
                            <ActionLink className="IconLink--copy" id="fertiliser-split" onClick={() => ungroupApplicationsConfirm()}>
                                <span>Split applications</span>
                            </ActionLink>
                        </li>
                        {process.env.REACT_APP_DISABLE_SCENARIO_TOOL !== "true" && (
                            <li>
                                <ScenarioToolLink farm={farm} analysis={analysis} />
                            </li>
                        )}
                    </WriteMode>
                    <li>
                        <ZendeskLink title="Page help" url="https://support.overseer.org.nz/hc/en-us/articles/900000962046" className="IconLink--new-tab u-font-green" />
                    </li>
                </ul>
            </div>

            <FertilisersGreenBox farm={farm} analysis={analysis} />

            {hasDCD && <AdditiveList farm={farm} analysis={analysis} />}

            {showQuickTips && <Alert type="help" text="Each block shows the total fertiliser applied each month. Click on the down arrow left of the block name to view each fertiliser application. Click on the block name to edit or add new applications to the block." />}

            {blockGroups.map((g, i) => (
                <BlockListGroup key={i} blockGroup={g} farm={farm} analysis={analysis} />
            ))}
            {blockList.nonProductiveBlocks.length > 0 && (
                <TileSubPanel title="Other blocks" stickyHeaderName="analysis.fertiliser.otherBlocks.collapsed">
                    {blockList.nonProductiveBlocks.map((block) => (
                        <BlockListCard key={block.id} block={block} farm={farm} analysis={analysis} />
                    ))}
                </TileSubPanel>
            )}
            {fertiliserProductModal}
        </>
    );
}
